import {
  AltiusPage,
  AltiusRes,
  IPaginationResponse,
  IPermissionItem,
  IUserManageData,
  IUserManageInvestmentEntity,
  ManageTeam
} from "store/types";
import { InvestEntity, IUserManage, IUserManageGroup, IUsersManageQueries } from "store/types";
import {
  IContact,
  IContactDealRole,
  IContactPayload,
  IContactsPage,
  IContactsQueries
} from "store/types/user-management";
import { delay } from "utils/delay";

import { AsyncActions } from "../base";

export interface IUsersActions {
  getUsersAction: (params: IUsersManageQueries) => Promise<IPaginationResponse<IUserManage>>;
  createUserManageAction: (data: IUserManageData) => Promise<void>;
  updateUserManageAction: (data: IUserManageData) => Promise<void>;
  deleteUserManageAction: (id: number) => Promise<void>;
  createInviteUserManageAction: (id: number) => Promise<void>;
  getInvestEntitiesAction: () => Promise<Array<InvestEntity>>;
  getUserAction: (id: number) => Promise<IUserManage>;
  getGroupsAction: () => Promise<IUserManageGroup[]>;
  createGroupAction: (name: string) => Promise<void>;
  updateGroupAction: (id: number, name: string) => Promise<void>;
  deleteGroupAction: (id: number) => Promise<void>;
  getManageTeamsAction: () => Promise<Array<ManageTeam>>;
  getUserPermissionListAction: () => Promise<IPermissionItem[]>;
  getInvestmentEntitiesAction: () => Promise<IUserManageInvestmentEntity[]>;
  getContactAction: (id: number) => Promise<IUserManage>;
  deleteContactAction: (id: number) => Promise<void>;
  updateContactAction: (params: IUserManageData) => Promise<void>;

  // Standalone CRM contacts CRUD (/api/v0.0.3/contacts), distinct from the user-management contact
  // methods above (getContactAction/updateContactAction/deleteContactAction) which operate on user
  // records at /user-management/contacts. Update/delete use patch/remove names to avoid that clash.
  getContactsAction: (queries: IContactsQueries) => Promise<IContactsPage>;
  createContactAction: (payload: IContactPayload) => Promise<IContact>;
  patchContactAction: (id: number, payload: IContactPayload) => Promise<IContact>;
  removeContactAction: (id: number) => Promise<void>;
  getContactDealRolesAction: () => Promise<IContactDealRole[]>;
}

export class UsersActions extends AsyncActions implements IUsersActions {
  public getUsersAction: IUsersActions["getUsersAction"] = async (params) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPaginationResponse<IUserManage>>>({
      url: "/api/v0.0.3/user-management/users",
      signal: this.addProcess("getUsersAction").signal,
      params: {
        ...params
      }
    });
    this.removeProcess("getUsersAction");
    return data;
  };

  public createUserManageAction: IUsersActions["createUserManageAction"] = async (params) => {
    await this.api.post<AltiusRes<IUserManage>>({
      url: "/api/v0.0.3/user-management/users",
      signal: this.addProcess("createUserManageAction").signal,
      data: {
        ...params
      }
    });
    this.removeProcess("createUserManageAction");
  };

  public updateUserManageAction: IUsersActions["updateUserManageAction"] = async (params) => {
    await this.api.patch<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${params.id}`,
      signal: this.addProcess("updateUserManageAction").signal,
      data: {
        ...params
      }
    });
    this.removeProcess("updateUserManageAction");
  };

  public deleteUserManageAction: IUsersActions["deleteUserManageAction"] = async (id) => {
    await this.api.delete<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${id}`
    });
  };

  public createInviteUserManageAction: IUsersActions["createInviteUserManageAction"] = async (id) => {
    await this.api.post<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${id}/resend-invite`
    });
  };

  public getUserPermissionListAction: IUsersActions["getUserPermissionListAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPermissionItem[]>>({
      url: "/api/v0.0.3/user-management/users-permissions"
    });
    return data;
  };

  public getInvestmentEntitiesAction: IUsersActions["getInvestmentEntitiesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManageInvestmentEntity[]>>({
      url: "/api/v0.0.3/user-management/investment-entities"
    });
    return data;
  };

  public getInvestEntitiesAction: IUsersActions["getInvestEntitiesAction"] = async () => {
    await delay(500);

    const result: InvestEntity[] = new Array(40).fill("").map((_, index) => ({
      id: index + 1,
      deal_name: `Deal name ${index + 1}`,
      parent_entities: ["First", "Second", "Third", "Fourth"],
      subordinated_entities: ["First", "Second", "Third", "Fourth"],
      users: new Array(10).fill("").map((_, index) => ({
        id: index + 1,
        profile_photo_url: "",
        role_id: 1,
        account_id: 1,
        full_name: "John Snow"
      }))
    }));
    return result;
  };

  public getUserAction: IUsersActions["getUserAction"] = async (id: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/users/${id}`,
      signal: this.addProcess("getUserAction").signal
    });
    this.removeProcess("getUserAction");
    return data;
  };

  public getGroupsAction: IUsersActions["getGroupsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManageGroup[]>>({
      url: "/api/v0.0.3/user-management/groups",
      signal: this.addProcess("getGroupsAction").signal
    });
    this.removeProcess("getGroupsAction");
    return data;
  };

  public createGroupAction: IUsersActions["createGroupAction"] = async (name: string) => {
    await this.api.post({
      url: "/api/v0.0.3/user-management/groups",
      data: {
        name
      },
      signal: this.addProcess("createGroupAction").signal
    });
    this.removeProcess("createGroupAction");
  };

  public updateGroupAction: IUsersActions["updateGroupAction"] = async (id: number, name: string) => {
    await this.api.post({
      url: `/api/v0.0.3/user-management/groups/${id}`,
      data: {
        _method: "PATCH",
        name
      },
      signal: this.addProcess("updateGroupAction").signal
    });
    this.removeProcess("updateGroupAction");
  };

  public deleteGroupAction: IUsersActions["deleteGroupAction"] = async (id: number) => {
    this.api.delete({
      url: `/api/v0.0.3/user-management/groups/${id}`,
      signal: this.addProcess("deleteGroupAction").signal
    });
    this.removeProcess("deleteGroupAction");
  };

  public getManageTeamsAction: IUsersActions["getManageTeamsAction"] = async () => {
    await delay(500);

    const result: ManageTeam[] = new Array(40).fill("").map((_, index) => ({
      id: index + 1,
      team_name: `Team ${index + 1}`,
      users: new Array(10).fill("").map((_, index) => ({
        id: index + 1,
        profile_photo_url: "",
        role_id: index % 2 === 0 ? 1 : 2,
        account_id: 1,
        full_name: `${index + 1}John Snow`,
        email: `${index + 1}JohnSnow@gmail.com`
      })),
      deals: new Array(15).fill("").map(() => ({
        id: index + 1,
        deal_name: `${index}test name`,
        parent_deal: `${index}parent deal`,
        sub_deals: [`${index}test name`, "subDeals2", "subDeals3"],
        other_teams: [`${index}test name`, "team2", "team3"]
      })),
      note: `${index + 1}some string`
    }));
    return result;
  };

  public getContactAction: IUsersActions["getContactAction"] = async (id: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/contacts/${id}`
    });
    return data;
  };

  public deleteContactAction: IUsersActions["deleteUserManageAction"] = async (id) => {
    await this.api.delete<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/contacts/${id}`
    });
  };

  public updateContactAction: IUsersActions["updateUserManageAction"] = async (params) => {
    await this.api.patch<AltiusRes<IUserManage>>({
      url: `/api/v0.0.3/user-management/contacts/${params.id}`,
      data: {
        ...params
      }
    });
  };

  // ----- Standalone CRM contacts (/api/v0.0.3/contacts) -----

  // List is paginated and doubly nested: AltiusRes<AltiusPage<IContact[]>>. Returns the inner
  // paginator (rows + total + per_page + current_page). Empty/undefined params are stripped.
  public getContactsAction: IUsersActions["getContactsAction"] = async (queries) => {
    const cleanedQueries = Object.fromEntries(
      Object.entries(queries).filter(([, v]) => v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0))
    );

    try {
      const {
        data: { data }
      } = await this.api.get<AltiusRes<AltiusPage<IContact[]>>>({
        url: "/api/v0.0.3/contacts",
        signal: this.addProcess("getContactsAction").signal,
        params: cleanedQueries
      });
      this.removeProcess("getContactsAction");
      return data;
    } catch (error) {
      this.removeProcess("getContactsAction");
      throw error;
    }
  };

  public createContactAction: IUsersActions["createContactAction"] = async (payload) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IContact>, IContactPayload>({
      url: "/api/v0.0.3/contacts",
      signal: this.addProcess("createContactAction").signal,
      data: payload
    });
    this.removeProcess("createContactAction");
    return data;
  };

  // True partial update — only the sent fields change. `deals` (when present) replaces the full set of
  // deal links (each with an optional deal_role); omit it to leave deal links untouched.
  public patchContactAction: IUsersActions["patchContactAction"] = async (id, payload) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<IContact>, IContactPayload>({
      url: `/api/v0.0.3/contacts/${id}`,
      signal: this.addProcess("patchContactAction").signal,
      data: payload
    });
    this.removeProcess("patchContactAction");
    return data;
  };

  // Soft delete — the contact vanishes from every endpoint (204 No Content).
  public removeContactAction: IUsersActions["removeContactAction"] = async (id) => {
    await this.api.delete<AltiusRes<null>>({
      url: `/api/v0.0.3/contacts/${id}`,
      signal: this.addProcess("removeContactAction").signal
    });
    this.removeProcess("removeContactAction");
  };

  // Reference list (key/label) for the per-deal role dropdown. Source the options from here, don't hardcode.
  public getContactDealRolesAction: IUsersActions["getContactDealRolesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IContactDealRole[]>>({
      url: "/api/v0.0.3/contacts/deal-roles",
      signal: this.addProcess("getContactDealRolesAction").signal
    });
    this.removeProcess("getContactDealRolesAction");
    return data;
  };
}
